<script lang="ts">
  import { page } from "@inertiajs/svelte"
  import { locale } from "svelte-i18n"

  import { BRAND_NAME } from "@/lib/brand"
  import { baseLanguage, hreflangLinks } from "@/lib/locale-routes"

  type Props = { title: string; description?: string }
  const { title, description }: Props = $props()

  // Google requires fully-qualified canonical/hreflang URLs. Crawlers read
  // the SSR output, where window doesn't exist — the server shares `origin`
  // (request.base_url) so the tags come out absolute; window covers any
  // client-only render.
  const origin = $derived(
    (page.props.origin as string | undefined) ??
      (typeof window !== "undefined" ? window.location.origin : ""),
  )
  const path = $derived((page.url ?? "/").split("?")[0])
  // Every marketing URL is self-canonical (the English home's is "/") — region
  // overlays are geo-resolved and never get URLs of their own.
  const canonical = $derived(origin + path)
  const alternates = $derived(hreflangLinks(origin, path))
  const ogImage = $derived(`${origin}/og-image.png`)

  const OG_LOCALES: Record<string, string> = {
    en: "en_US",
    de: "de_DE",
    es: "es_ES",
    it: "it_IT",
    fr: "fr_FR",
    pt: "pt_PT",
    tr: "tr_TR",
    pl: "pl_PL",
    uk: "uk_UA",
    ru: "ru_RU",
    ro: "ro_RO",
    ar: "ar_AR",
    fa: "fa_IR",
    cs: "cs_CZ",
    sq: "sq_AL",
    zh: "zh_CN",
    vi: "vi_VN",
    hi: "hi_IN",
    tl: "tl_PH",
  }
  const ogLocale = $derived(
    OG_LOCALES[baseLanguage($locale ?? "en")] ?? "en_US",
  )
</script>

<svelte:head>
  <title>{title}</title>
  {#if description}
    <meta name="description" content={description} />
  {/if}
  <link rel="canonical" href={canonical} />
  {#each alternates as alt (alt.hreflang)}
    <link rel="alternate" hreflang={alt.hreflang} href={alt.href} />
  {/each}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content={BRAND_NAME} />
  <meta property="og:title" content={title} />
  {#if description}
    <meta property="og:description" content={description} />
  {/if}
  <meta property="og:url" content={canonical} />
  <meta property="og:locale" content={ogLocale} />
  <meta property="og:image" content={ogImage} />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:image:alt" content={BRAND_NAME} />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={title} />
  {#if description}
    <meta name="twitter:description" content={description} />
  {/if}
  <meta name="twitter:image" content={ogImage} />
</svelte:head>
